import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { SearchBar } from "../../components/vc/SearchBar";
import { BusinessCard } from "../../components/vc/BusinessCard";
import { EmptyState, SkeletonList } from "../../components/vc/layout";
import { PrimaryButton } from "../../components/vc/Buttons";
import { FloatingButton } from "../../components/vc/FloatingButton";
import {
  Plus, Building2, QrCode, Share2, ScanLine, Search as SearchIcon, UserPlus, Truck, Copy, ArrowRight,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { ConnectionRequestsPanel } from "../../components/vc/ConnectionRequestsPanel";
import { QRScanner } from "../../components/vc/QRScanner";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { useNotifications } from "../../context/NotificationContext";

/**
 * Businesses screen — unified list of Customers + Suppliers.
 * Uses existing /api/customers + /api/suppliers endpoints; adds a QR/invite-code panel.
 */
export default function VCBusinesses() {
  const navigate = useNavigate();
  const { counts: notifCounts } = useNotifications();
  const bizCount = notifCounts.by_section.businesses;
  const prevBizCount = useRef(bizCount);
  const [q, setQ] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myCode, setMyCode] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [linkCode, setLinkCode] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [tab, setTab] = useState("all"); // all | connected | not_on_app
  const [scannerOpen, setScannerOpen] = useState(false);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    apiClient.get("/suppliers").then((r) => setSuppliers(r.data || []))
      .catch(() => setSuppliers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    apiClient.get("/me/supplier-code").then((r) => setMyCode(r.data.code)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Incoming order notification bugfix pattern reused here — a
  // connection_accepted notification (my outgoing request got approved)
  // should refresh this list to "Connected" automatically, with no manual
  // reload and no premature badge-clearing just from visiting this page.
  useEffect(() => {
    if (bizCount !== prevBizCount.current) {
      prevBizCount.current = bizCount;
      load(false);
    }
  }, [bizCount]);

  // Unified business list (no customer/supplier distinction)
  const merged = useMemo(() => {
    let all = suppliers.map((s) => ({
      id: s.id,
      raw_id: s.id,
      name: s.name,
      phone: s.phone,
      created_at: s.created_at,
      on_app: !!s.linked_owner_id,
      // #6 — connected businesses open the Portfolio view; unlinked ones stay
      // on the legacy supplier edit form.
      link: s.linked_owner_id ? `/portfolio/${s.linked_owner_id}` : `/suppliers/${s.id}`,
    }));
    if (tab === "connected") all = all.filter((b) => b.on_app);
    if (tab === "not_on_app") all = all.filter((b) => !b.on_app);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      all = all.filter((b) => (b.name || "").toLowerCase().includes(needle) || (b.phone || "").includes(needle));
    }
    return all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [suppliers, q, tab]);

  const connectedCount = suppliers.filter((s) => s.linked_owner_id).length;
  const notOnAppCount = suppliers.length - connectedCount;

  const shareQR = async () => {
    // Phase 7 #2 — a real clickable deep link, not just the bare code, so
    // the recipient lands directly on the pre-filled connect screen.
    const link = `${window.location.origin}/connect/${myCode}`;
    const text = `Join me on KonexoCRM! Tap to connect: ${link} (code: ${myCode})`;
    if (navigator.share) {
      try { await navigator.share({ text }); return; } catch (_e) { /* fallthrough to clipboard */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Invite link copied"); }
    catch { toast.error("Could not share"); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(myCode); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  const lookupCode = async () => {
    const c = linkCode.trim().toUpperCase();
    if (c.length < 4) { toast.error("Enter a valid code"); return; }
    setLinkBusy(true); setLinkPreview(null);
    try {
      const { data } = await apiClient.get(`/supplier-codes/${c}`);
      setLinkPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally { setLinkBusy(false); }
  };

  const confirmLink = async () => {
    setLinkBusy(true);
    try {
      await apiClient.post("/connection-requests", { code: linkCode.trim().toUpperCase() });
      toast.success("Connection request sent — waiting for the other business to accept");
      setShowAdd(false); setLinkCode(""); setLinkPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setLinkBusy(false); }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-4">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Businesses</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage all your connected businesses</p>
        </div>

        {/* Search + Add */}
        <div className="flex gap-2 items-center">
          <div className="flex-1"><SearchBar value={q} onChange={setQ} placeholder="Search business or phone" testid="businesses-search" /></div>
          <button
            data-testid="businesses-add-btn"
            onClick={() => setShowAdd(true)}
            className="h-11 px-4 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white flex items-center gap-1.5 text-sm font-semibold shadow-sm shadow-sky-500/30 transition"
          >
            <Plus className="w-4 h-4" strokeWidth={2.6} /> Add
          </button>
        </div>

        {/* Tabs — Connected Businesses / Not On App */}
        <div className="flex bg-slate-100 rounded-2xl p-1" data-testid="businesses-tabs">
          {[
            ["all", "All", suppliers.length],
            ["connected", "Connected", connectedCount],
            ["not_on_app", "Not On App", notOnAppCount],
          ].map(([v, l, count]) => (
            <button
              key={v}
              data-testid={`biz-tab-${v}`}
              onClick={() => setTab(v)}
              className={`flex-1 h-9 rounded-xl text-xs font-bold transition ${
                tab === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              {l} <span className="opacity-60">({count})</span>
            </button>
          ))}
        </div>

        {/* #5 Incoming connection requests — Accept / Reject */}
        <ConnectionRequestsPanel onChange={load} />

        {/* Business list */}
        <div className="space-y-2" data-testid="businesses-list">
          {loading ? <SkeletonList count={4} /> : merged.length === 0 ? (
            <EmptyState
              Icon={Building2}
              title={q ? "No matches" : "No businesses yet"}
              subtitle={q ? "Try a different search term" : "Add your first customer or supplier"}
              action={!q && (
                <PrimaryButton onClick={() => setShowAdd(true)} data-testid="empty-add-btn">
                  <Plus className="w-4 h-4 inline mr-1" /> Add Business
                </PrimaryButton>
              )}
            />
          ) : (
            merged.map((b) => (
              <BusinessCard
                key={b.id}
                business={b}
                to={b.link}
                testid={`business-${b.raw_id}`}
                onRemoved={() => load()}
              />
            ))
          )}
        </div>

        {/* QR card */}
        {myCode && (
          <div data-testid="qr-card" className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-3xl p-5 border border-emerald-100 shadow-sm mt-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-sm">
                <QrCode className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900">Your business QR</div>
                <div className="text-xs text-slate-500">Share so buyers can connect instantly</div>
              </div>
              <button
                onClick={() => navigate("/qr")}
                data-testid="biz-open-qr-library"
                className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5 shrink-0"
              >
                Manage <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-2xl border border-emerald-200 shrink-0" data-testid="biz-qr-canvas">
                <QRCodeCanvas
                  value={`konexo://connect?code=${myCode}`}
                  size={72}
                  level="M"
                  includeMargin={false}
                  fgColor="#065f46"
                  bgColor="#ffffff"
                />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <code data-testid="biz-my-code" className="block text-center text-lg font-bold tracking-widest text-emerald-900 bg-white rounded-xl border border-emerald-200 py-2">
                  {myCode}
                </code>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={copyCode}
                    data-testid="biz-copy-code"
                    className="h-9 rounded-xl bg-white border border-emerald-200 text-emerald-700 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-emerald-50 transition"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <button
                    onClick={shareQR}
                    data-testid="biz-share-qr"
                    className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1 transition"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <button
                    onClick={() => navigate("/portfolio")}
                    data-testid="biz-open-portfolio"
                    className="h-9 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-slate-50 transition"
                  >
                    <Building2 className="w-3.5 h-3.5" /> Portfolio
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <FloatingButton Icon={Plus} onClick={() => setShowAdd(true)} label="Add business" testid="businesses-fab" />

      {/* #3 native camera QR scanner — decodes konexo://connect?code=XXX */}
      <QRScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDecoded={async (code) => {
          // Close scanner + any lingering Add sheet before firing the request
          // so the page never shows a blank overlay after a successful scan.
          setScannerOpen(false);
          setShowAdd(false);
          setLinkBusy(true);
          try {
            await apiClient.post("/connection-requests", { code });
            toast.success("Connection request sent successfully.");
            // Refresh the list right away so the pending state is reflected...
            load();
            // ...then redirect to the Businesses page after the toast is
            // visible (~1.2s) — a clean re-navigation guarantees the scanner
            // overlay/video is fully torn down and never leaves a blank view.
            setTimeout(() => {
              navigate("/businesses", { replace: true });
            }, 1200);
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
          } finally { setLinkBusy(false); }
        }}
      />

      {/* Add Business bottom sheet */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) { setLinkCode(""); setLinkPreview(null); } }}>
        <DialogContent className="max-w-md rounded-3xl bg-white p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle>Add Business</DialogTitle>
            <DialogDescription>Connect a new business — via QR, code, or manually.</DialogDescription>
          </DialogHeader>

          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <QuickAction Icon={QrCode} label="Generate QR" onClick={() => { setShowAdd(false); navigate("/qr"); }} testid="add-generate-qr" />
              <QuickAction Icon={ScanLine} label="Scan QR" onClick={() => { setShowAdd(false); setScannerOpen(true); }} testid="add-scan-qr" />
              <QuickAction Icon={UserPlus} label="Add Business" onClick={() => { setShowAdd(false); navigate("/suppliers/new"); }} testid="add-business-manual" />
              <QuickAction Icon={Truck} label="Search Business" onClick={() => { setShowAdd(false); toast.info("Enter the invite code below to connect"); }} testid="add-search-business" />
            </div>

            <div className="pt-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Connect by code</div>
              <div className="flex gap-2">
                <Input
                  data-testid="add-code-input"
                  value={linkCode}
                  onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                  placeholder="e.g., HCQS4JMR"
                  className="h-11 rounded-xl text-base tracking-widest text-center font-mono uppercase"
                  maxLength={12}
                />
                <button
                  onClick={lookupCode}
                  disabled={linkBusy}
                  data-testid="add-code-lookup"
                  className="h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition"
                >
                  <SearchIcon className="w-4 h-4" />
                </button>
              </div>
              {linkPreview && (
                <div data-testid="add-code-preview" className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Found</div>
                  <div className="font-bold text-slate-900">{linkPreview.business_name || linkPreview.name}</div>
                </div>
              )}
              {linkPreview && (
                <PrimaryButton onClick={confirmLink} loading={linkBusy} data-testid="add-code-confirm" className="w-full mt-3">
                  Send connection request
                </PrimaryButton>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function QuickAction({ Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-100 transition"
    >
      <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shadow-sm">
        <Icon className="w-4 h-4 text-slate-700" strokeWidth={2.4} />
      </div>
      <span className="text-xs font-semibold text-slate-700">{label}</span>
    </button>
  );
}

// Lightweight visual QR placeholder (deterministic grid based on value string)
function QRCodePlaceholder({ value }) {
  const cells = 21;
  // hash-derived pattern
  let seed = 0;
  for (let i = 0; i < (value || "").length; i++) seed = (seed * 31 + value.charCodeAt(i)) >>> 0;
  const grid = [];
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      const idx = r * cells + c;
      const bit = ((seed >>> (idx % 32)) ^ ((idx * 2654435761) >>> 0)) & 1;
      // corner detection markers
      const isCorner = (r < 7 && c < 7) || (r < 7 && c >= cells - 7) || (r >= cells - 7 && c < 7);
      grid.push({ on: isCorner ? isCornerBit(r, c, cells) : bit });
    }
  }
  return (
    <div className="w-24 h-24 rounded-xl bg-white grid place-items-center p-1.5 shadow-sm border border-emerald-100 shrink-0">
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${cells}, 1fr)`, gridTemplateRows: `repeat(${cells}, 1fr)`, gap: "1px", width: "100%", height: "100%" }}
      >
        {grid.map((g, i) => (
          <div key={i} className={g.on ? "bg-emerald-900" : "bg-transparent"} />
        ))}
      </div>
    </div>
  );
}
function isCornerBit(r, c, cells) {
  // returns 1 for a stylized 7x7 finder pattern in the three corners
  const norm = (rr, cc) => {
    if (rr === 0 || rr === 6 || cc === 0 || cc === 6) return 1;
    if (rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4) return 1;
    return 0;
  };
  if (r < 7 && c < 7) return norm(r, c);
  if (r < 7 && c >= cells - 7) return norm(r, c - (cells - 7));
  if (r >= cells - 7 && c < 7) return norm(r - (cells - 7), c);
  return 0;
}
